import React, { useState, useEffect, useRef } from "react";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import Icon from "@amzn/meridian/esm/components/icon";
import errorTokens from "@amzn/meridian-tokens/base/icon/error";
import TranscribeBox from "./TranscribeBox";
import TranscribeBoxIndicator from "./TranscribeBoxIndicator";
import Loading from "../../components/Loading";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import zoomSdk from "@zoom/appssdk";
import AlertModal, { ALERT_VARIANT } from "../../components/alertModal/AlertModal";
import LogManager from "../../utils/logManager";
import { LEO_INTERVIEW_CHAT_MESSAGE, AIMS_START_DISCLAIMER_CHAT_MESSAGE } from "./transcript.constants";
import {
  TRANSCRIPTION_STATES,
  RTMS_REQUESTER_DEFAULT_NAME,
  MAIN_CONTEXT,
  AUTO_START_MODES,
} from "../../constants";
import { fetchRtmsSessionStatus, SESSION_STATUS, SESSION_STATUS_MESSAGE } from "../../utils/rtmsSessionStatusUtils";
import { fetchRtmsStatusUpdate } from "../../utils/rtmsStatusUpdateUtils";
import { THEME_COLOR } from "../../styles/colors";

/**
 * The available "Transcribe meeting" flow, shown to organizers / alternative
 * hosts in an AiMS-eligible meeting. Owns the RTMS session lifecycle: Zoom SDK
 * events + backend polling drive transcriptionState, and it renders the
 * Transcribe/End button (inside the shared TranscribeBox frame) plus the
 * status indicator below it.
 */
const TranscribeAvailable = ({
  role,
  meetingId,
  meetingUuid,
  alias,
  appContext,
  initialIsRtmsStartedByOther,
  initialRtmsRequesterName,
  transcriptionState,
  setTranscriptionState,
  isRestarting,
  setIsRestarting,
  isAutostarting,
  setIsAutostarting,
  autoStartMode,
  leoInterviewer,
}) => {
  // Modal states
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);
  const [isRtmsErrorModalOpen, setIsRtmsErrorModalOpen] = useState(false);

  // Data states
  const [rtmsErrorType, setRtmsErrorType] = useState(null);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(null);
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(false);

  const isPerHostTranscription = autoStartMode === AUTO_START_MODES.MULTIPLE;

  const hideStoppedComponentTimerRef = useRef(null);
  const rtmsStartTimestampRef = useRef(null);
  const sessionStatusIntervalRef = useRef(null);
  const sessionStatusTimeoutRef = useRef(null);
  const rejectionTimeoutRef = useRef(null);
  // Tracks whether the current user initiated the RTMS start (for transcript flow)
  const userInitiatedStartRef = useRef(false);
  // Tracks the rtmsSessionId from the Zoom SDK "started" event so we can match "stopped" events
  // to this user's own session in per-host transcription mode
  const rtmsSessionIdRef = useRef(null);

  /**
   * Handle RTMS status change events from Zoom SDK
   * @param {object} event - The RTMS status change event
   */
  async function handleRTMSChange(event) {
    LogManager.info("RTMS changed:", JSON.stringify(event));
    LogManager.info(`[TranscribeAvailable] RTMS status changed to: ${event.status}`);

    if (event.status && event.status === "started") {
      if (isPerHostTranscription) {
        // Per-host transcription: only react if THIS user initiated the start
        if (!userInitiatedStartRef.current) {
          LogManager.info("[TranscribeAvailable] RTMS started by another user — ignoring for per-host transcription");
          return;
        }
        userInitiatedStartRef.current = false;
        setIsRtmsStartedByOther(false);
        sendTranscriptionStartToChat();
        // Save the rtmsSessionId so we can match stopped events to this session
        if (event.rtmsSessionId) {
          rtmsSessionIdRef.current = event.rtmsSessionId;
          LogManager.info(`[TranscribeAvailable] Saved rtmsSessionId: ${event.rtmsSessionId}`);
        }
      } else {
        // Single-stream: check session status to determine who started
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          LogManager.info("Session status when started:", sessionStatus);
          if (!sessionStatus.rtmsRequesterAlias) {
            setIsRtmsStartedByOther(true);
            setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
          } else if (sessionStatus.rtmsRequesterAlias !== alias) {
            setIsRtmsStartedByOther(true);
            setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
          } else {
            setIsRtmsStartedByOther(false);
            sendTranscriptionStartToChat();
          }
        } catch (error) {
          LogManager.error("Error fetching session status on RTMS start:", error);
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
        }
      }

      // Set transcribing state and record timestamp
      setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
      // Clear auto start flag when transcription actually starts
      setIsAutostarting(false);

      // Record the timestamp when transcription starts
      const startTimestamp = Math.floor(Date.now() / 1000);
      rtmsStartTimestampRef.current = startTimestamp;
      LogManager.info("RTMS started at timestamp:", startTimestamp);
    } else if (event.status && event.status === "stopped") {
      if (isPerHostTranscription) {
        // Per-host transcription: only process stop events for this user's own session.
        // If we never started a session (no stored rtmsSessionId), ignore all stop events.
        if (!rtmsSessionIdRef.current) {
          LogManager.info("[TranscribeAvailable] No active session — ignoring stopped event for per-host transcription");
          return;
        }
        // Match on rtmsSessionId from the Zoom SDK event against the one we saved on start.
        if (event.rtmsSessionId && event.rtmsSessionId !== rtmsSessionIdRef.current) {
          LogManager.info(`[TranscribeAvailable] RTMS stopped for different session (${event.rtmsSessionId}) — ignoring`);
          return;
        }
        rtmsSessionIdRef.current = null;
      }
      // Reset isRtmsStartedByOther immediately so organizers and alt hosts can see the transcribe button
      setIsRtmsStartedByOther(false);
      setRtmsRequesterName(null);

      // Set to STOPPED state immediately
      setTranscriptionState(TRANSCRIPTION_STATES.STOPPED);

      // Fetch session status to check for errors and get reviewer data
      fetchRtmsSessionStatus(meetingUuid).then((sessionData) => {
        LogManager.info(`Session status: ${sessionData.status}, statusMessage: ${sessionData.statusMessage}`);


        if (sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR ||
            sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
          // Show error modal for init or runtime errors only if current user started RTMS
          if (!isRtmsStartedByOther) {
            setRtmsErrorType(sessionData.statusMessage);
            setIsRtmsErrorModalOpen(true);
          }
        } else {
          // Show TranscribeStopped component for 2 minutes
          hideStoppedComponentTimerRef.current = setTimeout(() => {
            setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
          }, 120000); // 2 minutes
        }
      }).catch(error => {
        LogManager.error("Error fetching session status:", error);
        // Fallback to normal behavior if status fetch fails
        setTranscriptionState(TRANSCRIPTION_STATES.STOPPED);
      });
    }
  }

  //TODO: add more checks
  const onEndTranscribe = async () => {
    if (transcriptionState === TRANSCRIPTION_STATES.LOADING) return;
    setTranscriptionState(TRANSCRIPTION_STATES.LOADING);
    LogManager.info("End button clicked, call stopping RTMS API...");

    let rtmsResponse = {};
    try {
      rtmsResponse = await fetchRtmsStatusUpdate("stop", meetingUuid, meetingId, role, appContext);
    } catch (e) {
      console.error(e);
      rtmsResponse = "Error";
    }

    if (rtmsResponse.result !== "OK") {
      setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT); // Reset to default state on error
      LogManager.info(`RTMS stop request failed with result: ${rtmsResponse.result}, showing server error modal`);
      setIsServerErrorModalOpen(true);
      return;
    }

    // Note: State changes (transcriptionState updates, transcription status checking)
    // are now handled automatically by the handleRTMSChange event listener
    // Loading state will be cleared when RTMS status change event is received
    LogManager.info("RTMS stop request successful. State changes will be handled by event listener.");

    // Don't clear loading state here for successful requests - let handleRTMSChange do it
  };

  const sendTranscriptionStartToChat = async () => {
    try {
      await zoomSdk.sendMessageToChat({
        message: AIMS_START_DISCLAIMER_CHAT_MESSAGE
      });
      LogManager.info("Sent transcription disclaimer message");
      if (leoInterviewer) {
        await zoomSdk.sendMessageToChat({
          message: LEO_INTERVIEW_CHAT_MESSAGE
        });
        LogManager.info("Sent LEO interview reminder message");
      }
    } catch (error) {
      LogManager.error("Error sending disclaimer message:", error);
    }
  };

  const handleRtmsErrorCancel = () => {
    // Cancel: Close modal and reset to original state (show Transcribe button)
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    // Reset all states to original
    setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
    // Clear requester information
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    LogManager.info("User cancelled RTMS error modal - reset to original state");
  };

  const handleRtmsErrorRestart = () => {
    // Restart: Close modal and restart transcription
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    // Reset states and restart
    setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
    // Mark as restarting
    setIsRestarting(true);
    // Clear requester information before restarting
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    LogManager.info("User clicked restart from RTMS error modal - restarting transcription");
    // Start transcription immediately
    onTranscribe(true);
  };

  //TODO: Add more checks
  const onTranscribe = async (isRestartAttempt = false, isAutostart = false) => {
    if (transcriptionState === TRANSCRIPTION_STATES.LOADING) return;

    // Clear any pending timers from a previous session
    if (hideStoppedComponentTimerRef.current) {
      clearTimeout(hideStoppedComponentTimerRef.current);
      hideStoppedComponentTimerRef.current = null;
    }

    // Only set loading state if not auto starting (auto start shows full-page loader)
    if (!isAutostart) {
      setTranscriptionState(TRANSCRIPTION_STATES.LOADING);
    }
    LogManager.info("IsRestarting: " + isRestartAttempt);
    if (isRestartAttempt) {
      LogManager.info("User clicked restart - waiting 45 seconds for backend timeout before sending new request");

      // Wait 45 seconds (first attempt already waited 15s, total 60s for backend timeout)
      await new Promise(resolve => setTimeout(resolve, 45000));

      // Now send a fresh start request
      LogManager.info("45 seconds elapsed, sending new RTMS start request");
    }
    LogManager.info("Attempting to start RTMS...");
    // Mark that the current user is initiating the start (for transcript flow)
    userInitiatedStartRef.current = true;

    let rtmsResponse = {};
    try {
      rtmsResponse = await fetchRtmsStatusUpdate("start", meetingUuid, meetingId, role, appContext);
    } catch (e) {
      LogManager.error("Error calling /rtms-status-update:", e);
      rtmsResponse = "Error";
    }

    LogManager.info("The /rtms-status-update response:", rtmsResponse);

    if (rtmsResponse.result === "OK") {
      // Start polling session status every 5 seconds
      LogManager.info("RTMS start request successful. Starting session status polling...");

      // Clear any existing intervals/timeouts
      if (sessionStatusIntervalRef.current) {
        clearInterval(sessionStatusIntervalRef.current);
      }
      if (sessionStatusTimeoutRef.current) {
        clearTimeout(sessionStatusTimeoutRef.current);
      }

      // Set up polling every 5 seconds
      sessionStatusIntervalRef.current = setInterval(async () => {
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          LogManager.info(`Session status polling result: ${sessionStatus.status}, statusMessage: ${sessionStatus.statusMessage}`);

          if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
            // Status is now active, update state
            clearInterval(sessionStatusIntervalRef.current);
            clearTimeout(sessionStatusTimeoutRef.current);
            setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
            LogManager.info("Session status is now ACTIVE, stopping polling");
          } else if (sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR ||
                     sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
            // Error detected during polling, show error modal
            clearInterval(sessionStatusIntervalRef.current);
            clearTimeout(sessionStatusTimeoutRef.current);
            userInitiatedStartRef.current = false;
            setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
            setRtmsErrorType(sessionStatus.statusMessage);
            setIsRtmsErrorModalOpen(true);
            LogManager.info(`RTMS error detected during polling: ${sessionStatus.statusMessage}, showing error modal`);
          }
        } catch (error) {
          LogManager.error("Error polling session status:", error);
          // Clear autostart flag on polling error
          setIsAutostarting(false);
        }
      }, 5000);

      // Set up timeout to show error modal if still INACTIVE (15s for first attempt, 60s for restart)
      const timeoutDuration = isRestartAttempt ? 60000 : 15000;
      sessionStatusTimeoutRef.current = setTimeout(async () => {
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          if (sessionStatus.status !== SESSION_STATUS.ACTIVE) {
            // Still inactive after timeout, show error modal
            clearInterval(sessionStatusIntervalRef.current);
            userInitiatedStartRef.current = false;
            setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
            setRtmsErrorType(SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR);
            setIsRtmsErrorModalOpen(true);
            // Clear autostart flag so error modal can be displayed
            setIsAutostarting(false);
            LogManager.info(`Session status still INACTIVE after ${timeoutDuration/1000} seconds, showing error modal`);
          }
        } catch (error) {
          LogManager.error("Error checking final session status:", error);
          clearInterval(sessionStatusIntervalRef.current);
          userInitiatedStartRef.current = false;
          setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
          // Clear autostart flag on final status check error
          setIsAutostarting(false);
          setIsServerErrorModalOpen(true);
        }
      }, timeoutDuration);
    } else if (rtmsResponse.result === "REJECTED") {
      // Someone else already started the transcription
      userInitiatedStartRef.current = false;
      setTranscriptionState(TRANSCRIPTION_STATES.REJECTED);
      const requesterName = rtmsResponse.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME;
      setRtmsRequesterName(requesterName);
      setIsAutostarting(false);
      LogManager.info(`RTMS start rejected - ${requesterName} is already starting transcription`);

      // Set up 2-minute timer to reset to Transcribe button if no RTMS "started" event is received
      rejectionTimeoutRef.current = setTimeout(() => {
        LogManager.info("2-minute rejection timeout reached, resetting to Transcribe button");
        setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
        setRtmsRequesterName(null);
      }, 120000); // 2 minutes
    } else {
      // Handle all other error cases (FAIL, Error, etc.)
      userInitiatedStartRef.current = false;
      setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT); // Clear loading state on error
      // Clear autostart flag on RTMS start failure
      setIsAutostarting(false);
      setIsServerErrorModalOpen(false);
      setTimeout(() => setIsServerErrorModalOpen(true), 0);
    }
  };

  // Initialize state with values from Home.jsx when component mounts
  useEffect(() => {
    if (initialIsRtmsStartedByOther !== undefined) {
      setIsRtmsStartedByOther(initialIsRtmsStartedByOther);
    }
    if (initialRtmsRequesterName !== undefined) {
      setRtmsRequesterName(initialRtmsRequesterName);
    }
  }, [initialIsRtmsStartedByOther, initialRtmsRequesterName]);

  // Set up Zoom SDK listener - must run before auto-start
  useEffect(() => {
    // Set up RTMS status change listener
    try {
      LogManager.info(`[TranscribeAvailable] Setting up RTMS status change listener for meeting: ${meetingUuid}`);
      zoomSdk.onRTMSStatusChange(handleRTMSChange);
    } catch (error) {
      LogManager.error("[TranscribeAvailable] Error setting up RTMS status listener:", error);
    }

    // Cleanup function to clear the timeout when component unmounts
    return () => {
      if (hideStoppedComponentTimerRef.current) {
        clearTimeout(hideStoppedComponentTimerRef.current);
      }
      if (sessionStatusIntervalRef.current) {
        clearInterval(sessionStatusIntervalRef.current);
      }
      if (sessionStatusTimeoutRef.current) {
        clearTimeout(sessionStatusTimeoutRef.current);
      }
      if (rejectionTimeoutRef.current) {
        clearTimeout(rejectionTimeoutRef.current);
      }
    };
  }, [meetingUuid]);

  // Auto-clear isRestarting when we leave LOADING state
  useEffect(() => {
    if (transcriptionState !== TRANSCRIPTION_STATES.LOADING && isRestarting) {
      setIsRestarting(false);
    }
  }, [transcriptionState]);

  // Handle auto-start transcription - runs once on mount after SDK listener is set up
  useEffect(() => {
    if (isAutostarting) {
      LogManager.info("Auto start triggered, starting transcription automatically...");
      onTranscribe(false, true);
    }
  }, []);

  // Full-page loader while auto starting.
  if (isAutostarting) {
    return <Loading label="Starting transcription..." />;
  }

  return (
    <Theme palette={aimsTheme}>
      <Column alignmentHorizontal="center" spacing="none" minHeight="630px">
        <Column spacing="500" spacingInset="600 none none none" width="226px" alignmentHorizontal="start">
          {/* Hide the transcribe button box if session is started by someone else */}
          {!isRtmsStartedByOther && (
              <TranscribeBox>
                <Theme
                  tokens={{
                    buttonBackgroundColorPrimaryDefault: transcriptionState === TRANSCRIPTION_STATES.TRANSCRIBING ? THEME_COLOR.CRIMSON : THEME_COLOR.TEAL_GREEN,
                    buttonBackgroundColorPrimaryHover: transcriptionState === TRANSCRIPTION_STATES.TRANSCRIBING ? THEME_COLOR.CRIMSON : THEME_COLOR.DEEP_TEAL,
                    buttonBackgroundColorPrimaryPressed: transcriptionState === TRANSCRIPTION_STATES.TRANSCRIBING ? THEME_COLOR.DARK_MAROON : THEME_COLOR.DARKEST_TEAL,
                  }}
                >
                  {transcriptionState === TRANSCRIPTION_STATES.LOADING ? (
                    <Row alignmentHorizontal="center">
                      <Theme palette={aimsTheme}>
                        <Loader size="medium"/>
                      </Theme>
                    </Row>
                  ) : transcriptionState === TRANSCRIPTION_STATES.TRANSCRIBING ? (
                    <Button size="small" minWidth="194px" onClick={onEndTranscribe}>
                      End
                    </Button>
                  ) : transcriptionState === TRANSCRIPTION_STATES.REJECTED ? (
                    <Row alignmentHorizontal="center">
                      <Icon tokens={errorTokens} color="error" />
                    </Row>
                  ) : (
                    // DEFAULT and STOPPED states both show the Transcribe button
                    <Button size="small" minWidth="194px" onClick={onTranscribe}>
                      Transcribe
                    </Button>
                  )}
                </Theme>
                {isServerErrorModalOpen && (
                  <AlertModal variant={ALERT_VARIANT.SERVER_NOT_RESPONDING} isOpen={true} onClose={() => setIsServerErrorModalOpen(false)} context={MAIN_CONTEXT.TRANSCRIBE} />
                )}
                {isRtmsErrorModalOpen &&
                  <AlertModal
                    variant={ALERT_VARIANT.RTMS_ERROR}
                    isOpen={isRtmsErrorModalOpen}
                    onCancel={handleRtmsErrorCancel}
                    onRestart={handleRtmsErrorRestart}
                    errorType={rtmsErrorType}
                    context={MAIN_CONTEXT.TRANSCRIBE}
                  />
                }
              </TranscribeBox>
          )}
          {/* TranscribeBoxIndicator: state-specific UI (warnings, status messages, etc.) */}
          <TranscribeBoxIndicator
            transcriptionState={transcriptionState}
            rtmsRequesterName={rtmsRequesterName}
            isRtmsStartedByOther={isRtmsStartedByOther}
            isRestarting={isRestarting}
          />
        </Column>
      </Column>
    </Theme>
  );
};

export default TranscribeAvailable;
